import { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuthStore } from '@/store/authStore';
import { useToast } from '@/hooks/useToast';
import { ShieldCheck, Activity, Cpu } from 'lucide-react';

export function AuthCallbackPage() {
  const navigate = useNavigate();
  const { initialize } = useAuthStore();
  const { success, error } = useToast();

  useEffect(() => {
    const handleAuthCallback = async () => {
      try {
        // Since we're using backend auth, check for auth tokens in URL params
        const urlParams = new URLSearchParams(window.location.search);
        const token = urlParams.get('token');
        const userId = urlParams.get('user_id');

        if (token && userId) {
          // Store the auth data and initialize
          localStorage.setItem('auth_token', token);
          localStorage.setItem('user_id', userId);

          await initialize();
          success('Authentication Successful', 'Welcome back!');
          navigate('/dashboard');
        } else {
          // No auth data found, redirect to login
          navigate('/login');
        }
      } catch (err) {
        error('Authentication Failed', 'Unable to complete authentication. Please try again.');
        navigate('/login');
      }
    };

    handleAuthCallback();
  }, [navigate, initialize, success, error]);

  return (
    <div className="min-h-screen bg-gray-50 flex flex-col items-center justify-center p-6">
      <a href="#main-content" className="sr-only focus:not-sr-only focus:absolute focus:top-4 focus:left-4 focus:z-[100] focus:px-4 focus:py-2 focus:bg-primary-600 focus:text-white focus:rounded-lg focus:shadow-lg min-h-touch">
        Skip to main content
      </a>
      <main id="main-content" role="main" className="text-center">
        <div className="relative mb-8">
          <div className="w-20 h-20 bg-primary-50 border-2 border-primary-200 rounded-xl flex items-center justify-center mx-auto shadow-sm">
            <ShieldCheck className="w-10 h-10 text-primary-600 animate-pulse" />
          </div>
        </div>

        <div className="space-y-3">
          <h1 className="text-2xl font-bold text-gray-900">
            Authenticating...
          </h1>
          <p className="text-gray-600 text-sm">
            Please wait while we verify your credentials
          </p>
        </div>

        <div className="mt-8 flex items-center justify-center gap-2">
          <div className="w-2 h-2 bg-primary-600 rounded-full animate-bounce" style={{ animationDelay: '0ms' }} />
          <div className="w-2 h-2 bg-primary-600 rounded-full animate-bounce" style={{ animationDelay: '150ms' }} />
          <div className="w-2 h-2 bg-primary-600 rounded-full animate-bounce" style={{ animationDelay: '300ms' }} />
        </div>
      </main>
    </div>
  );
}